import React from 'react';
import { 
  Container, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  Paper, 
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { styled } from '@mui/system';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  paddingLeft: 0,
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export default function Usage() {
  return (
    <Container maxWidth="lg">
      <Box my={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          FocusTrack 新規ユーザー向けガイド
        </Typography>
        
        <Typography variant="body1" paragraph>
          以下では、「FocusTrack」を正式名称としたタスク管理 × ポモドーロ × レポート機能を備えるアプリケーションの新規ユーザー向け詳細ガイドを示します。
          モバイルブラウザでも利用は可能ですが、PCでのデスクワーク利用を主な想定として設計されています（モバイルはあくまでおまけ的な使い方）。
        </Typography>

        <StyledAccordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">1. アプリ概要</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              FocusTrack は以下の要素を一体化したアプリです。
            </Typography>
            <List>
              <StyledListItem>
                <ListItemText 
                  primary="タスク管理" 
                  secondary="タスクタイトル・詳細・子タスク・優先度・締切日などを設定し、進捗を可視化"
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="ポモドーロ計測" 
                  secondary="25分作業＋5分休憩など「ポモドーロテクニック」を使って集中作業を管理"
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="レポート機能" 
                  secondary="タスク完了数・ポモドーロ実施数などの履歴をグラフ表示し、振り返りや自己分析が可能"
                />
              </StyledListItem>
            </List>
          </AccordionDetails>
        </StyledAccordion>

        <StyledAccordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">2. タスク管理の基本操作</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              ログイン成功後、メイン画面にタスクリストが表示されます。
            </Typography>
            <List>
              <StyledListItem>
                <ListItemText 
                  primary="新規タスク作成" 
                  secondary={
                    <>
                      「新規タスク追加」ボタンを押し、タイトル・説明・優先度・タグなど入力<br/>
                      締切日(Deadline)を指定しておくと管理しやすい<br/>
                      保存ボタン
                    </>
                  }
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="子タスク(チェックリスト)" 
                  secondary={
                    <>
                      タスクの詳細を開き、「子タスクを追加」ボタン<br/>
                      子タスクごとに完了チェックが可能
                    </>
                  }
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="ステータス更新" 
                  secondary={
                    <>
                      タスクのステータスを pending / in-progress / completed / cancelled などに切り替え<br/>
                      作業中は in-progress、終わったら completed など運用する
                    </>
                  }
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="アーカイブ/削除" 
                  secondary={
                    <>
                      完了やキャンセル済みタスクを「アーカイブ」画面に送る<br/>
                      再利用したい場合はアーカイブからコピー可能
                    </>
                  }
                />
              </StyledListItem>
            </List>
          </AccordionDetails>
        </StyledAccordion>

        <StyledAccordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">3. ポモドーロ計測（集中タイマー）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              <StyledListItem>
                <ListItemText 
                  primary="ポモドーロ開始" 
                  secondary="タスクリストで対象タスクを選び、「ポモドーロ開始」ボタン（25分タイマーがスタート）"
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="通知（Push通知）" 
                  secondary={
                    <>
                      作業終了 or 休憩開始タイミングで通知を受け取れる（初回のみブラウザ通知許可が必要）<br/>
                      PCでのデスクトップ通知がメイン想定だが、モバイルでも通知される場合あり
                    </>
                  }
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="計測終了" 
                  secondary={
                    <>
                      25分終了時に休憩アラート → 終了報告でセッション情報をサーバーに保存<br/>
                      休憩→次のポモドーロ開始…と繰り返す
                    </>
                  }
                />
              </StyledListItem>
            </List>
          </AccordionDetails>
        </StyledAccordion>

        <StyledAccordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">4. アーカイブ画面</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              <StyledListItem>
                <ListItemText 
                  primary="完了タスク一覧" 
                  secondary="status=completed や cancelled のタスクが表示"
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="コピー機能" 
                  secondary="過去のタスクをコピーして新しいタスクを生成"
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="検索/絞り込み" 
                  secondary="タグやタイトル、完了日などでフィルタ"
                />
              </StyledListItem>
            </List>
          </AccordionDetails>
        </StyledAccordion>

        <StyledAccordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">5. レポート（統計）画面</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              <StyledListItem>
                <ListItemText 
                  primary="期間別表示" 
                  secondary="1日/7日/28日などのポモドーロ数・完了タスク数を折れ線グラフで確認"
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="タグごとの完了率" 
                  secondary={
                    <>
                      円グラフでタグ別の完了タスク数を表示<br/>
                      上位3タグ＋ Others として集計
                    </>
                  }
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="平均評価" 
                  secondary="ポモドーロ終了時に付けた星評価などがあれば、その平均値を表示"
                />
              </StyledListItem>
            </List>
          </AccordionDetails>
        </StyledAccordion>

        <StyledAccordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">6. 通知設定</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              <StyledListItem>
                <ListItemText 
                  primary="設定画面" 
                  secondary="「通知を有効にする」スイッチをON"
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="ブラウザ通知許可ダイアログ" 
                  secondary="「このサイトからの通知を許可しますか？」 → '許可'"
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="購読期限切れ対策" 
                  secondary="たまに購読期限が切れて通知が届かなくなる場合あり。再度ログイン時に購読状態をチェック→再購読することがある"
                />
              </StyledListItem>
            </List>
          </AccordionDetails>
        </StyledAccordion>

        <StyledAccordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">7. よくある質問 (FAQ)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              <StyledListItem>
                <ListItemText 
                  primary="PCで使うのが前提？" 
                  secondary="はい。PCでのデスクワークを想定しており、画面レイアウトもPC向き。モバイルでも動作は可能だが簡易利用程度です。"
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="通知が来ないときは？" 
                  secondary={
                    <>
                      ブラウザで通知がブロックされている可能性 → 設定を見直す<br/>
                      購読が期限切れ → ログイン時に自動再購読 or 手動再許可を行う
                    </>
                  }
                />
              </StyledListItem>
              <StyledListItem>
                <ListItemText 
                  primary="長期間のデータ参照" 
                  secondary="レポート画面の期間を28日以上にカスタムしたい場合は、今後の拡張予定(例)"
                />
              </StyledListItem>
            </List>
          </AccordionDetails>
        </StyledAccordion>

        <StyledPaper elevation={3}>
          <Typography variant="h5" component="h2" gutterBottom>
            まとめ
          </Typography>
          <List>
            <StyledListItem>
              <ListItemText primary="主なユースケース: 新規登録 → ログイン → タスク作成 → ポモドーロ計測 → アーカイブ & レポート" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="モバイルでも一応利用可だが、PCでのデスクワークに最適" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Push通知でポモドーロ終了時にアラートを受け取り、集中作業がより効率化できる" />
            </StyledListItem>
          </List>
        </StyledPaper>
      </Box>
    </Container>
  );
}

